import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Datatable from "../../components/common/datatable";
import SearchItem from "../../components/searchItem";
import Pagination from "../../components/common/pagination";
import { useViewActivityLog } from "../../hooks/activityLog/activityLog.view";
import { getPageTitle } from "../../utils/getPageTitle";
import "../../assets/styles/table.css";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const truncate = (text, max = 48) => {
  if (!text) return "-";
  const str = String(text);
  return str.length > max ? `${str.slice(0, max)}…` : str;
};

const getStatusClass = (status) => {
  const value = String(status || "").toLowerCase();
  if (
    value === "success" ||
    value === "200" ||
    value === "201" ||
    value.includes("ok")
  ) {
    return "status-active";
  }
  if (
    value === "error" ||
    value === "failed" ||
    value === "failure" ||
    value.startsWith("4") ||
    value.startsWith("5")
  ) {
    return "status-rejected";
  }
  if (value === "pending") return "status-pending";
  return "status-default";
};

const ActivityLog = () => {
  const location = useLocation();
  const [localLogs, setLocalLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const {
    data: activityLogResponse,
    isLoading,
    isFetching,
    isError,
  } = useViewActivityLog({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!activityLogResponse) return;
    setLocalLogs(activityLogResponse.activityLogs ?? []);
    const apiPagination = activityLogResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [activityLogResponse, pageSize]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const columns = [
    {
      key: "createdAt",
      label: "Date",
      render: (_, row) =>
        formatDate(row.createdAt || row.created_at || row.timestamp),
    },
    {
      key: "userRole",
      label: "User Role",
      render: (_, row) => row.userRole || row.role || row.user?.role || "-",
    },
    {
      key: "action",
      label: "Action",
      render: (value) => value || "-",
    },
    {
      key: "description",
      label: "Description",
      render: (value) => truncate(value, 60),
    },
    {
      key: "ip",
      label: "IP Address",
      render: (_, row) => row.ip || row.ipAddress || row.ip_address || "-",
    },
    {
      key: "method",
      label: "Method",
      render: (value) => (value ? String(value).toUpperCase() : "-"),
    },
    {
      key: "status",
      label: "Status",
      render: (_, row) => {
        const status =
          row.status ?? row.statusCode ?? row.status_code ?? row.httpStatus;
        if (status == null || status === "") return "-";
        const label = String(status);
        return (
          <span className={`status-badge ${getStatusClass(label)}`}>
            {label}
          </span>
        );
      },
    },
    {
      key: "userAgent",
      label: "User Agent",
      render: (_, row) => truncate(row.userAgent || row.user_agent, 40),
    },
  ];

  if (isLoading) {
    return <div className="page-body">Loading activity logs...</div>;
  }

  if (isError) {
    return <div className="page-body">Error loading activity logs.</div>;
  }

  return (
    <>
      <div className="page-body">
        <div className="page-header activitylog-header">
          <div>
            <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
            <p style={{ color: "rgb(85, 85, 85)", fontSize: "13px" }}>
              View platform activity and audit events
            </p>
          </div>
        </div>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localLogs}
            searchField={[
              "userRole",
              "role",
              "action",
              "description",
              "ip",
              "ipAddress",
              "method",
              "status",
              "userAgent",
            ]}
            placeholder="Search activity logs..."
            wrapSection={false}
            onResultsChange={({ searchItem }) => setSearchTerm(searchItem)}
          />
          <label className="list-toolbar-page-size">
            <span>Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>
      </div>

      <div className="table-container" style={{ marginTop: 16 }}>
        <Datatable data={localLogs} columns={columns} />
      </div>

      {isFetching && !isLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default ActivityLog;
